import { createStore } from 'effector';

import {
  ADD_PAYMENT_TODO,
  ADD_VEHICLE_TODO,
  CONFIRM_VISIT,
  PAYMENT_OVERDUE_TODO,
  SELECT_VISIT_DURATION_TODO,
  ADD_PHONE_NUMBER_TODO,
  CONFIRM_MISSED_ENTRY_START_VISIT_TODO,
} from '@m/constants/TodoTypes';
import { applyReducers } from '@m/effector-utils';
import { logout } from 'apps/customer/state/user';

import { completeTask, fetchTodoItems } from './actions';

import type { DoneHandler } from '@m/effector-utils';
import type {
  AddPaymentFollowup,
  AddVehicleFollowup,
  ConfirmVisitFollowup,
  SelectVisitDurationFollowUp,
  TodoItem,
  TodoItemType,
  AddPhoneNumberFollowup,
  MissedEntryStartVisitFollowUp,
  SelectVehicleFollowUp,
} from '@m/types/api';
import type { APIResponse } from '@m/utils/http';
import type { Effect, Event } from 'effector';

export type OnboardingTasks = {
  addPayment?: AddPaymentFollowup;
  addPhoneNumber?: AddPhoneNumberFollowup;
  addVehicle?: AddVehicleFollowup;
  confirmVisit?: ConfirmVisitFollowup; // only available in v2
  list: TodoItem[];
  missedEntryStartVisit?: MissedEntryStartVisitFollowUp;
  selectVehicle?: SelectVehicleFollowUp;
  selectVisitDuration?: SelectVisitDurationFollowUp;
};

export type TodoState = {
  currentTaskType: TodoItemType | null;
  expressOnboardingExperimentGroup: string | null;
  hasFetchedTodo: boolean;
  hasPaymentTask: boolean | null;
  hasPendingTasks: boolean | null;
  hasRequiredTasks: boolean | null;
  onboardingTasks: OnboardingTasks | null;
  todoItems: TodoItem[] | null;
};

const initialState: TodoState = {
  todoItems: null,
  onboardingTasks: null,
  currentTaskType: null,
  expressOnboardingExperimentGroup: null,
  hasPendingTasks: null,
  hasPaymentTask: null,
  hasRequiredTasks: null,
  hasFetchedTodo: false,
};

const store = createStore(initialState);

type Reducers = {
  completeTask: {
    action: Event<TodoItemType>;
    reducer: (state: TodoState, payload: TodoItemType) => TodoState;
  };
  fetchTodoItems: {
    action: Effect<Parameters<typeof fetchTodoItems>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof fetchTodoItems>[0], TodoState>;
  };
};

export function setOnboardingTasks(todoItems: TodoItem[]): OnboardingTasks {
  const onboardingTasks: Omit<OnboardingTasks, 'list'> = todoItems.reduce((curr, todo) => {
    if (todo.type === ADD_PHONE_NUMBER_TODO) {
      return {
        ...curr,
        addPhoneNumber: todo,
      };
    }
    if (todo.type === ADD_VEHICLE_TODO) {
      return {
        ...curr,
        addVehicle: todo,
      };
    }
    if (todo.type === ADD_PAYMENT_TODO) {
      return {
        ...curr,
        addPayment: todo,
      };
    }
    if (todo.type === SELECT_VISIT_DURATION_TODO) {
      return {
        ...curr,
        selectVisitDuration: todo,
      };
    }
    if (todo.type === CONFIRM_VISIT) {
      return {
        ...curr,
        confirmVisit: todo,
      };
    }
    if (todo.type === CONFIRM_MISSED_ENTRY_START_VISIT_TODO) {
      return {
        ...curr,
        missedEntryStartVisit: todo,
      };
    }
    return {
      ...curr,
    };
  }, {});

  return {
    ...onboardingTasks,
    list: todoItems,
  };
}

export const getFormatTodoItemData = (todoItems: TodoItem[]) => ({
  todoItems,
  onboardingTasks: setOnboardingTasks(todoItems),
  currentTaskType: todoItems && todoItems[0] ? todoItems[0].type : null,
  hasPendingTasks: !!todoItems?.length,
  hasPaymentTask: !!todoItems.find((todoItem) => todoItem.type === PAYMENT_OVERDUE_TODO),
  hasRequiredTasks: !!todoItems.find((todoItem) => todoItem.required),
  hasFetchedTodo: true,
});

export const reducers: Reducers = {
  fetchTodoItems: {
    action: fetchTodoItems,
    done: (state, { result: { success, data } }) => {
      if (success) {
        return {
          ...state,
          ...getFormatTodoItemData(data.todoItems),
          expressOnboardingExperimentGroup:
            data.expressOnboardingExperimentGroup ?? state.expressOnboardingExperimentGroup,
        };
      }
      return state;
    },
  },
  completeTask: {
    action: completeTask,
    reducer: (state, type) => {
      let nextTodoItems: TodoItem[] = [];
      if (state.todoItems) {
        nextTodoItems = state.todoItems.filter((todoItem) => todoItem.type !== type);
      }

      return {
        ...state,
        ...getFormatTodoItemData(nextTodoItems),
      };
    },
  },
};

const asyncResetReducers = [logout.done];

export default applyReducers({ storeName: 'customer/todo', store, reducers, asyncResetReducers });
